import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';

import {
  JobBoardCodename,
  JobBoardElementTranslations,
  JobBoardElementTranslationsToken,
  JobBoardType,
  OfferFull,
  SettingsLink,
} from '@recruitee/http/types';
import { I18nService } from '@recruitee/i18n';

@Component({
  selector: 'rt-job-board-element',
  templateUrl: './job-board-element.component.html',
  styleUrls: ['./job-board-element.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobBoardElementComponent {
  public isPending: boolean = false;
  public readonly JobBoardType = JobBoardType;

  @Input() public name: string;
  @Input() public codename: string;
  @Input() public type: JobBoardType;
  @Input() public label: string;
  @Input() public logoUrl: string;
  @Input() public website: string;
  @Input() public isTrial: boolean;
  @Input() public posting: { status: string; url: string } | null;
  @Input() public canToggle: boolean;
  @Input() public tooltipMessage: string | null;
  @Input() public autoPostingDisabled: boolean;
  @Input() public settingsLink: SettingsLink | null;
  @Input() public pendingPosting: boolean;
  @Input() public offer: OfferFull;

  @Output() public moreInfo: EventEmitter<{
    title: string;
    description: string;
  }> = new EventEmitter();
  @Output() public postingAdd: EventEmitter<void> = new EventEmitter<void>();
  @Output() public openIntercom: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    @Inject(JobBoardElementTranslationsToken)
    public translations: JobBoardElementTranslations,
    private cdRef: ChangeDetectorRef,
    private i18n: I18nService,
  ) {}

  public get showPublish(): boolean {
    return (
      this.codename !== JobBoardCodename.Glassdoor &&
      !this.autoPostingDisabled &&
      this.canToggle &&
      !this.pendingPosting &&
      !this.posting
    );
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.posting) {
      this.isPending = this.posting?.status === 'pending_review';
    }
  }

  public onAddPosting(): void {
    if (this.isPending) return;

    if (this.codename === JobBoardCodename.MyJobBoardHelper && this.offer.countryCode !== 'US') {
      this.moreInfo.emit({
        title: this.name,
        description: this.i18n.translate('app.promote.my-job-helper.us_based_validation'),
      });
    } else if (this.codename === JobBoardCodename.CodyGr) {
      if (this.offer.countryCode !== 'GR' && !this.offer.remote) {
        this.moreInfo.emit({
          title: this.name,
          description: this.i18n.translate('app.promote.cody-gr.gr_based_validation'),
        });
      } else if (
        !['engineering', 'information_technology', 'internet', 'telecommunication'].includes(
          this.offer.category!,
        )
      ) {
        this.moreInfo.emit({
          title: this.name,
          description: this.i18n.translate('app.promote.cody-gr.category_based_validation'),
        });
      } else {
        this.postingAdd.emit();
      }
    } else if (this.codename === JobBoardCodename.Jobissim && this.offer.countryCode !== 'FR') {
      this.moreInfo.emit({
        title: this.name,
        description: this.i18n.translate('app.promote.jobissim.fr_based_validation'),
      });
    } else {
      this.postingAdd.emit();
    }
    this.cdRef.detectChanges();
  }

  public onMoreInfo(): void {
    if (this.codename === JobBoardCodename.Fashionunited) {
      this.moreInfo.emit({
        title: this.name,
        description: this.i18n.translate('app.promote.fashionunited.contact_message', {
          interpolatesHTML: true,
        }),
      });
    }
    this.cdRef.detectChanges();
  }
}
